// iter180f — Public legal pages (Privacy + Terms).
// Shared chrome — premium hospitality treatment, NO auth required, NO Layout.
// Mounted at /legal/privacy and /legal/terms. Used by:
//   • Google OAuth consent screen verification (required by Google when
//     publishing the app for external users).
//   • App footer + "Customer Pulse Index" public guest pages.
//   • Anyone signing up for RestroPulse.
import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, ShieldCheck, FileText } from 'lucide-react';

export function LegalShell({ title, eyebrow, icon: Icon, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-[#FAFBF8]">
      {/* Top bar */}
      <header className="border-b border-[#E2E8DF] bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1A2421] hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg bg-[color:var(--color-primary)] text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <span className="text-[15px] font-semibold tracking-tight">RestroPulse</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[12px] text-[#5C6B64] hover:text-[color:var(--color-primary)] transition-colors"
            data-testid="legal-back-to-app"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
            Back to app
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        {/* Eyebrow */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-md bg-[color:var(--color-primary)]/8 text-[color:var(--color-primary)] flex items-center justify-center">
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
            {eyebrow}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-[34px] sm:text-[40px] font-semibold text-[#1A2421] tracking-tight leading-tight mb-2">
          {title}
        </h1>
        <p className="text-[12px] text-[#9AAA9F]">Last updated: {lastUpdated}</p>

        {/* Divider */}
        <div className="h-px bg-[#E2E8DF] my-8" />

        {/* Long-form content */}
        <article className="prose prose-sm max-w-none text-[14.5px] text-[#1A2421] leading-relaxed
                            [&_h2]:text-[20px] [&_h2]:font-semibold [&_h2]:text-[#1A2421] [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-3
                            [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:text-[#1A2421] [&_h3]:mt-6 [&_h3]:mb-2
                            [&_p]:text-[#3A4541] [&_p]:mb-4
                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:mb-4 [&_ul]:text-[#3A4541]
                            [&_a]:text-[color:var(--color-primary)] [&_a]:underline [&_a]:underline-offset-2">
          {children}
        </article>

        {/* Footer / cross-link */}
        <div className="mt-16 pt-8 border-t border-[#E2E8DF] flex flex-wrap items-center justify-between gap-3 text-[12px] text-[#5C6B64]">
          <div className="flex items-center gap-4">
            <Link to="/legal/privacy" className="hover:text-[color:var(--color-primary)] transition-colors">Privacy Policy</Link>
            <span className="text-[#D4D9D2]">·</span>
            <Link to="/legal/terms" className="hover:text-[color:var(--color-primary)] transition-colors">Terms of Service</Link>
          </div>
          <span>© {new Date().getFullYear()} RestroPulse · All rights reserved</span>
        </div>
      </main>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell
      eyebrow="Privacy"
      title="Privacy Policy"
      icon={ShieldCheck}
      lastUpdated="September 2026"
    >
      <p>
        RestroPulse ("we", "us", "our") provides a multi-property hospitality operations platform —
        Customer Pulse Index, financial intelligence, operational dashboards, and connected
        third-party integrations including Google Business Profile. This Privacy Policy explains
        what data we collect, how we use it, the third parties we integrate with, and the rights
        you have over your information.
      </p>

      <h2>1. Information we collect</h2>
      <h3>Account &amp; tenant data</h3>
      <ul>
        <li>Name, work email, role, tenant (organisation) you belong to.</li>
        <li>Hashed authentication credentials. We never store passwords in plain text.</li>
        <li>Outlet / property metadata you configure inside RestroPulse.</li>
      </ul>

      <h3>Operational data you submit</h3>
      <ul>
        <li>Financial entries, checklists, recipes, inventory counts, marketing entries, customer
          feedback submissions and any other content you record in the app.</li>
        <li>Customer Pulse Index (CPI) guest submissions — these are submitted by your guests via
          QR-driven public forms. Your tenant alone controls and owns this data.</li>
      </ul>

      <h3>Telemetry &amp; security logs</h3>
      <ul>
        <li>Login attempts, IP address, browser user-agent, and timestamps for audit + abuse
          prevention.</li>
        <li>Application error reports (no sensitive payload data) so we can fix bugs.</li>
      </ul>

      <h3>Public demo &amp; prospective-customer data</h3>
      <p>
        When you request access to the RestroPulse live demo at{' '}
        <a href="/demo">/demo</a>, we collect the following to grant secure access and
        (only where you explicitly opt in) to follow up with you:
      </p>
      <ul>
        <li>Work email address.</li>
        <li>Mobile number and phone country code.</li>
        <li>The demo persona you selected — Owner / Admin or Manager.</li>
        <li>Whether you ticked the follow-up consent checkbox, and the timestamp of that consent.</li>
        <li>Access timestamps (first access, last access, number of accesses).</li>
        <li>Salted hashes of IP address and browser user-agent, used solely for abuse detection.</li>
        <li>One-time access code (OTP) and demo-session metadata — never the OTP itself
          in plaintext; only an HMAC hash we cannot reverse.</li>
        <li>Campaign attribution: <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">utm_source</code>,{' '}
          <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">utm_medium</code>,{' '}
          <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">utm_campaign</code>,{' '}
          <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">utm_content</code>,{' '}
          <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">utm_term</code>,
          and the HTTP referrer that brought you to the demo.</li>
      </ul>
      <p>We use this information only for the following purposes:</p>
      <ul>
        <li>Provide secure, rate-limited access to the read-only demo environment.</li>
        <li>Deliver the one-time access code by email so you can enter the demo.</li>
        <li>Detect and prevent abuse, fraud, and automated attacks on the demo.</li>
        <li>Understand demo usage in aggregate so we can improve the product story.</li>
        <li>Attribute marketing campaigns that brought a visitor to the demo.</li>
        <li>Contact you <strong>by phone</strong> about RestroPulse{' '}
          <strong>only</strong> where you have explicitly ticked the follow-up consent
          checkbox — never by default.</li>
      </ul>
      <p>
        <strong>Requesting demo access does not constitute email-marketing consent.</strong>{' '}
        Providing your mobile number does <strong>not by itself</strong> permit sales
        follow-up. Sales follow-up happens only when you explicitly opt in via the
        follow-up consent checkbox, and the flag{' '}
        <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">marketing_email_consent</code>{' '}
        is always initialised to <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">false</code>{' '}
        for every demo record. You can withdraw follow-up consent at any time (see
        Section 8 below).
      </p>

      <h2>2. Google Business Profile integration</h2>
      <p>
        Where a tenant connects their Google Business Profile to RestroPulse, we request the
        OAuth scope <code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">https://www.googleapis.com/auth/business.manage</code>.
        This authorisation is granted by the individual admin who completes Google's consent
        screen, and can be revoked by them at any time from their{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
          Google Account permissions page
        </a>.
      </p>
      <p>We use Google Business Profile data <strong>only</strong> for the following purposes:</p>
      <ul>
        <li>Discovering the list of business locations the connecting admin manages, so they can
          map each Google location to a RestroPulse outlet.</li>
        <li>Fetching customer reviews, ratings, reviewer names, review timestamps and existing
          replies for the locations the admin has explicitly mapped.</li>
        <li>Posting review replies on behalf of the tenant <strong>only</strong> when the admin
          composes and submits the reply inside RestroPulse.</li>
      </ul>
      <p>
        We do <strong>not</strong> use Google Business Profile data to train AI models, sell to
        third parties, advertise, build user-level behavioural profiles, or share with any party
        outside the tenant who authorised the connection. Refresh tokens are encrypted at rest in
        our database and are never exposed to the browser or to any third party. If a tenant
        disconnects their Google account from RestroPulse, all tokens and synchronised review
        data for that tenant are deleted within 30 days.
      </p>
      <p>
        RestroPulse's use and transfer of information received from Google APIs adheres to the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
          Google API Services User Data Policy
        </a>, including the Limited Use requirements.
      </p>

      <h2>3. How we use your data</h2>
      <ul>
        <li>To deliver the features you sign up for — dashboards, P&amp;L, CPI, inventory, etc.</li>
        <li>To enforce role-based access inside your tenant.</li>
        <li>To send essential service emails (password resets, security alerts, billing).</li>
        <li>To investigate and prevent abuse, fraud, and security incidents.</li>
        <li>To comply with legal obligations.</li>
      </ul>

      <h2>4. Data sharing &amp; service providers</h2>
      <p>
        We do <strong>not sell</strong> tenant data, customer data, demo-visitor data, or
        any data obtained from Google APIs to anyone. We share data only with:
      </p>
      <ul>
        <li><strong>Cloud hosting, database, and infrastructure providers</strong> that run
          the RestroPulse platform under confidentiality and data-processing agreements,
          solely to operate the service.</li>
        <li><strong>Transactional email delivery provider</strong> — used to send the
          public-demo one-time access code, password-reset emails, security alerts and
          service notifications. The provider only receives the recipient address, subject
          and message body needed to deliver the email.</li>
        <li><strong>Cloudflare Turnstile</strong> — the bot / abuse-protection challenge on
          the public demo page. Cloudflare receives only the minimum browser signal needed
          to score the challenge; RestroPulse never sends your email or mobile number to
          Cloudflare.</li>
        <li><strong>Analytics / security processors</strong> — where applicable, aggregate,
          non-identifying telemetry and error-report processors that help us keep the
          platform reliable and safe. These processors do not receive tenant financial
          data or CPI guest submissions.</li>
        <li><strong>Authorised users within your tenant</strong> — admins, area managers,
          and outlet managers see the slice of data their role permits.</li>
        <li><strong>Legal authorities</strong> if compelled by valid legal process, and
          only the minimum required.</li>
      </ul>

      <h2>5. Data retention</h2>
      <ul>
        <li>Operational and financial data: retained for the active life of your tenant + 7 years
          for statutory record-keeping (financial regulation).</li>
        <li>CPI guest submissions: retained for the active life of your tenant + 24 months unless
          you delete earlier.</li>
        <li>Google Business Profile tokens + synchronised reviews: deleted within 30 days of
          disconnection or tenant termination.</li>
        <li>Telemetry logs: 90 days.</li>
        <li><strong>Public-demo OTP challenges, active demo sessions, and rate-limit
          counters:</strong> automatically deleted at the end of their technical window
          (OTP: 10 minutes; demo session: 45 minutes; rate-limit windows: 1 hour) via
          database TTL indexes.</li>
        <li><strong>Public-demo / prospective-customer contact records
          (<code className="font-mono text-[12.5px] bg-[#EEF4EF] px-1.5 py-0.5 rounded">demo_access_leads</code>):</strong>{' '}
          demo and prospective-customer information is generally retained for up to
          <strong> 12 months after the last interaction</strong>, subject to active
          commercial discussions, ongoing customer relationships, legal or compliance
          requirements, and any earlier deletion request where applicable law grants
          that right. Deletion is currently executed on request — the underlying
          database does not automatically delete these records. To have your demo
          record deleted, contact us using the details in Section 10.</li>
      </ul>

      <h2>6. Security</h2>
      <ul>
        <li>All traffic is encrypted in transit via TLS 1.2+.</li>
        <li>Tokens and secrets are encrypted at rest.</li>
        <li>Every API endpoint enforces per-tenant isolation server-side. A user from Tenant A
          can never query Tenant B's data, even via direct API calls.</li>
        <li>Role-based access control + feature governance is enforced both in the UI and at the
          API layer.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>
        Subject to applicable law, you have the right to:
      </p>
      <ul>
        <li><strong>Access</strong> the personal information we hold about you.</li>
        <li><strong>Correct</strong> inaccurate or outdated details.</li>
        <li><strong>Delete</strong> your personal data where deletion is available under
          applicable law and no legal / compliance obligation requires us to keep it
          (see Section 5).</li>
        <li><strong>Withdraw follow-up consent</strong> given on the public-demo form —
          reply to any RestroPulse email or contact us at the address in Section 10 with
          the request "withdraw demo follow-up consent". We will stop follow-up
          communications and record the withdrawal.</li>
        <li><strong>File a privacy grievance</strong> or complaint about how we handle
          your data.</li>
      </ul>
      <p>
        To exercise any of these rights, contact us at the address in Section 10. We
        will respond to privacy requests within the timeframe required by applicable law.
      </p>

      <h3>Public-demo follow-up consent</h3>
      <p>
        On the public-demo page you may tick a checkbox that reads:
      </p>
      <blockquote className="border-l-2 border-[#143E30] pl-4 italic text-[#3A4541]">
        "I agree that RestroPulse may contact me by phone regarding this demo and its
        products or services."
      </blockquote>
      <p>
        This checkbox is optional and unchecked by default. It authorises phone follow-up
        only. It is distinct from — and does <strong>not</strong> imply — marketing-email
        consent. You may withdraw it at any time using the process above.
      </p>

      <h2>8. International transfers</h2>
      <p>
        Our infrastructure may process data in jurisdictions different from yours. Where required
        by law (e.g. GDPR), transfers are governed by Standard Contractual Clauses or equivalent
        safeguards.
      </p>

      <h2>9. Changes</h2>
      <p>
        We will post material changes to this policy with at least 30 days' notice via the
        "Last updated" date and an in-app notification.
      </p>

      <h2>10. Contact</h2>
      <p>
        RestroPulse is currently operated by <strong>Lokesha V, Founder &amp; CEO</strong>,
        pending formal business registration. All privacy, grievance, and legal
        correspondence should be addressed to:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:restropulsesystems@gmail.com">restropulsesystems@gmail.com</a></li>
        <li><strong>Phone:</strong> +91 73494 42077</li>
      </ul>
      <p className="text-[13px] text-[#5C6B64]">
        Formal legal-entity details (registered company name, address, and
        registration numbers) will be added here once RestroPulse's business
        registration is completed.
      </p>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell
      eyebrow="Terms"
      title="Terms of Service"
      icon={FileText}
      lastUpdated="September 2026"
    >
      <p>
        These Terms of Service ("Terms") govern your access to and use of RestroPulse. By signing
        in or otherwise using the service, you agree to these Terms on behalf of yourself and the
        tenant you represent.
      </p>

      <h2>1. The service</h2>
      <p>
        RestroPulse is a multi-property hospitality operations platform. Features include
        financial dashboards (P&amp;L, Cost Insights, 5-Minute Dashboard), Customer Pulse Index
        (QR-driven guest feedback), operational checklists, inventory, recipe and production
        modules, communication, marketing, and optional connected integrations including Google
        Business Profile.
      </p>

      <h2>2. Eligibility &amp; accounts</h2>
      <ul>
        <li>You must be at least 18 years old and authorised by your tenant to use the service.</li>
        <li>You are responsible for keeping your credentials confidential and for all activity
          under your account.</li>
        <li>Tenant administrators are responsible for provisioning and de-provisioning their
          users.</li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Reverse-engineer, decompile, or attempt to extract source code from the service.</li>
        <li>Use the service to harass, defraud, or violate the rights of others.</li>
        <li>Upload malicious code or attempt to disrupt the service.</li>
        <li>Use the service or any connected third-party data (including Google Business Profile
          data) for purposes that violate the third party's terms — e.g. you may not use Google
          Reviews data to spam guests, train AI models, or build behavioural profiles.</li>
      </ul>

      <h2>4. Third-party integrations</h2>
      <p>
        When you connect a third-party service (such as Google Business Profile), you grant
        RestroPulse the limited authority needed to perform the operations you've configured
        (e.g. fetch reviews, post replies you compose). Your continued use of those integrations
        is subject to the third party's terms. We cannot guarantee third-party availability or
        data accuracy.
      </p>

      <h2>5. Data ownership</h2>
      <ul>
        <li>You retain all rights to data you upload or submit ("Tenant Data").</li>
        <li>You grant RestroPulse the limited licence necessary to host, process, and display
          Tenant Data solely to operate the service for your tenant.</li>
        <li>Aggregated, fully anonymised metrics may be used by RestroPulse to improve the
          service.</li>
      </ul>

      <h2>6. Customer Pulse Index (CPI) guest submissions</h2>
      <p>
        When your guests submit feedback via your tenant's CPI QR codes, those submissions are
        Tenant Data and are controlled by your tenant. You are responsible for ensuring you have
        legal grounds to collect guest data (e.g. signage, consent flows) and for handling guest
        rights requests under applicable privacy law.
      </p>

      <h2>7. Fees &amp; billing</h2>
      <p>
        Fees, plans, and payment terms are defined in your service order. Unless otherwise
        agreed, fees are charged in advance and non-refundable for the period paid.
      </p>

      <h2>8. Suspension &amp; termination</h2>
      <ul>
        <li>You may terminate by notifying us in writing. Tenant Data export is available for
          30 days after termination.</li>
        <li>We may suspend or terminate for material breach (e.g. acceptable use violations,
          non-payment) with reasonable notice where practicable.</li>
        <li>Upon termination, we delete or anonymise Tenant Data within 30 days, except as
          required by law (e.g. financial record-keeping obligations).</li>
      </ul>

      <h2>9. Warranties &amp; disclaimers</h2>
      <p>
        We provide the service on an "as is" and "as available" basis. To the maximum extent
        permitted by law, we disclaim all implied warranties, including merchantability, fitness
        for a particular purpose, and non-infringement.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, neither party shall be liable for any indirect,
        incidental, special, consequential, or punitive damages. Each party's aggregate liability
        under these Terms shall not exceed the fees paid to RestroPulse in the 12 months
        preceding the claim.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        Each party shall indemnify the other against third-party claims arising from its breach
        of these Terms, its negligence, or its violation of applicable law.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which RestroPulse is
        incorporated. Disputes shall be resolved exclusively in the competent courts of that
        jurisdiction, unless local consumer protection law mandates otherwise.
      </p>

      <h2>13. Public demo and evaluation access</h2>
      <p>
        RestroPulse operates a public demo at <a href="/demo">/demo</a>. The demo is
        intended solely for product evaluation and the following additional terms apply
        to your use of it:
      </p>
      <ul>
        <li>All financials, outlets, guests, inventory, checklists, marketing content and
          expansion analyses shown in the demo (including <em>The Amber Kitchen Group</em>)
          are <strong>fictional / illustrative demonstration data</strong>. They do not
          represent any real business, and no real customer information is exposed
          through the demo.</li>
        <li>Demo access is <strong>time-limited</strong> (currently a 45-minute session
          after email verification). Sessions may expire earlier if a new demo request is
          made from the same email.</li>
        <li>The public demo is <strong>read-only</strong>. Attempts to create, modify,
          upload, export, send, or configure anything are blocked server-side.</li>
        <li>RestroPulse may modify, reset, throttle, or withdraw demo access at any time,
          for any user or in aggregate, without notice.</li>
        <li>You may not scrape, attack, automate, reverse-engineer, or attempt to bypass
          the demo's access controls, rate limits, bot-protection challenge (Cloudflare
          Turnstile), OTP flow, or read-only enforcement.</li>
        <li>Demo access creates <strong>no service-level commitment, warranty, or
          production-service obligation</strong>. Nothing shown in the demo is a promise
          about the production RestroPulse service.</li>
        <li>Demo visitors receive <strong>no access to real customer or tenant
          information</strong>.</li>
      </ul>

      <h2>14. Expansion Intelligence and analytics disclaimer</h2>
      <p>
        RestroPulse Expansion Intelligence (probes, city intelligence reports, competitor
        enrichment, saved sample reports) and other analytical outputs across the platform
        (dashboards, P&amp;L insights, forecasts, recommendations) are{' '}
        <strong>business decision-support tools</strong>. They are provided to help you
        think about business questions — not to replace independent professional advice.
      </p>
      <p>Outputs from these tools do <strong>not</strong> constitute:</p>
      <ul>
        <li>financial advice,</li>
        <li>investment advice,</li>
        <li>legal advice,</li>
        <li>real-estate or property advice,</li>
        <li>tax or regulatory advice.</li>
      </ul>
      <p>
        You remain solely responsible for independent validation of any output and for
        the final business decision. In particular, for the public demo:{' '}
        <strong>the Mumbai / Carter Road expansion report is a sample demonstration report
        and is not a current recommendation to invest in, lease, or open a restaurant at
        that location.</strong>
      </p>

      <h2>15. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes will be notified at least
        30 days in advance via the "Last updated" date and an in-app notification. Continued use
        after the effective date constitutes acceptance.
      </p>

      <h2>16. Contact</h2>
      <p>
        RestroPulse is currently operated by <strong>Lokesha V, Founder &amp; CEO</strong>,
        pending formal business registration. Questions about these Terms should be
        addressed to:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:restropulsesystems@gmail.com">restropulsesystems@gmail.com</a></li>
        <li><strong>Phone:</strong> +91 73494 42077</li>
      </ul>
      <p className="text-[13px] text-[#5C6B64]">
        Formal legal-entity details will be added here once RestroPulse's business
        registration is completed.
      </p>
    </LegalShell>
  );
}
